'use client';
import type { NextPage } from 'next';
import { useMemo, useState, useRef, type CSSProperties, FormEvent } from 'react';
import { useRouter } from 'next/navigation';
import { useForm, SubmitHandler } from 'react-hook-form';
import styles from './booking-form.module.scss';
import { BookingFormType, BookingFormData, CalculatePriceResponse, FlightSummary } from '@/types/heroBlock.types';
import CurrentTab1 from './components.tsx/currentTab1';
import CurrentTab2 from './components.tsx/currentTab2';
import CurrentTab3 from './components.tsx/currentTab3';
import { ErrorModal } from '@/components';
import { getCountryCode } from '@/utils/createWorldCitiesOptions';
import { getAttributionPayload } from '@/utils/getAttributionPayload';
type TabType = 'one-way' | 'round-trip' | 'multi-city' | 'empty-leg';

const bookingTypeMap: Record<TabType, string> = {
  'one-way': 'oneWay',
  'round-trip': 'roundTrip',
  'multi-city': 'multiCity',
  'empty-leg': 'oneWay',
};

const toISODateTime = (date: string, time: string): string => {
  const parsed = new Date(`${date} ${time}`);
  if (!isNaN(parsed.getTime())) return parsed.toISOString();
  return `${date}T${time.slice(0, 5)}:00Z`;
};

const buildRouteLocation = (location: string): Record<string, string> => {
  const trimmed = location.trim();

  // Bare IATA/ICAO code typed manually: "LAX", "KLAX"
  if (/^[A-Z]{3,4}$/.test(trimmed)) {
    return { iata: trimmed };
  }

  // Single airport or specific airport selected: "Odessa, Ukraine(ODS)" / "Miami Intl Airport (MIA)"
  // The title ends with "(IATA)" — 3–4 uppercase letters in parens
  const iataMatch = trimmed.match(/\(([A-Z]{3,4})\)\s*$/);
  if (iataMatch) {
    return { iata: iataMatch[1] };
  }

  // Multi-airport city: "Miami, United States (All airports)"
  const allAirportsMatch = trimmed.match(/^(.+?),\s*(.+?)\s*\(All airports\)\s*$/i);
  if (allAirportsMatch) {
    const city = allAirportsMatch[1].trim();
    const countryName = allAirportsMatch[2].trim();
    const country = getCountryCode(countryName) || countryName;
    return { city, country };
  }

  // "City, Country" without suffix
  const cityCountryMatch = trimmed.match(/^(.+?),\s*(.+)$/);
  if (cityCountryMatch) {
    const city = cityCountryMatch[1].trim();
    const countryName = cityCountryMatch[2].trim();
    const country = getCountryCode(countryName) || countryName;
    return { city, country };
  }

  return { city: trimmed };
};

const buildPricePayload = (
  data: BookingFormData,
  tab: TabType,
  multiInputs: string[]
) => {
  const passengersCount = parseInt(data.passengers, 10) || 1;

  let route: Record<string, string>[] = [];

  if (tab === 'one-way') {
    route = [
      { ...buildRouteLocation(data.fromLocation), departure_time: toISODateTime(data.departureDate, data.departureTime) },
      { ...buildRouteLocation(data.toLocation) },
    ];
  } else if (tab === 'round-trip') {
    route = [
      { ...buildRouteLocation(data.fromLocation), departure_time: toISODateTime(data.departureDate, data.departureTime) },
      { ...buildRouteLocation(data.toLocation), departure_time: toISODateTime(data.returnDate, data.returnTime) },
    ];
  } else if (tab === 'multi-city') {
    const cities = multiInputs.filter((c) => c.trim() !== '');
    route = cities.map((city, index) => ({
      ...buildRouteLocation(city),
      // Every city except the final destination gets a departure_time
      ...(index < cities.length - 1
        ? {
            departure_time: toISODateTime(
              data.multiCityDates[index] || '',
              data.multiCityTimes[index] || ''
            ),
          }
        : {}),
    }));
  }

  const payload: Record<string, unknown> = {
    route,
    booking_type: bookingTypeMap[tab],
    passengers_count: passengersCount,
    verbose: false,
  };

  if (data.typeAircraft) {
    payload.aircraft_category = data.typeAircraft
      .toLowerCase()
      .replace(/[-\s]+/g, '_');
  }

  return payload;
};

const BookingForm: NextPage<BookingFormType> = ({
  className = '',
  device = 'Desktop',
  tabsMargin,
  tabActivePadding,
  tabActiveMinWidth,
  tabDefaultPadding,
  tabDefaultMinWidth,
  tabDefaultPadding1,
  tabDefaultMinWidth1,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('one-way');
  const [currentFormTab, setCurrentFormTab] = useState<number>(1);
  const [isMultiCity, setIsMultiCity] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [priceData, setPriceData] = useState<CalculatePriceResponse | null>(null);
  const [flightSummary, setFlightSummary] = useState<FlightSummary | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const router = useRouter();

  const {
    control,
    handleSubmit: onSubmit,
    reset,
    watch,
    setValue,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<BookingFormData>({
    defaultValues: {
      fromLocation: '',
      toLocation: '',
      passengers: '',
      departureDate: '',
      departureTime: '',
      typeAircraft: '',
      multiCityInputs: ['', '', ''],
      multiCityDates: ['', ''],
      multiCityTimes: ['', ''],
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      returnDate: '',
      returnTime: '',
    },
    mode: 'onChange',
    reValidateMode: 'onBlur',
  });

  const multiCityInputs = watch('multiCityInputs');

  const tabsStyle: CSSProperties = useMemo(() => {
    return {
      margin: tabsMargin,
    };
  }, [tabsMargin]);

  const tabActiveStyle: CSSProperties = useMemo(() => {
    return {
      padding: tabActivePadding,
      minWidth: tabActiveMinWidth,
    };
  }, [tabActivePadding, tabActiveMinWidth]);

  const tabDefaultStyle: CSSProperties = useMemo(() => {
    return {
      padding: tabDefaultPadding,
      minWidth: tabDefaultMinWidth,
    };
  }, [tabDefaultPadding, tabDefaultMinWidth]);

  const tabDefault1Style: CSSProperties = useMemo(() => {
    return {
      padding: tabDefaultPadding1,
      minWidth: tabDefaultMinWidth1,
    };
  }, [tabDefaultPadding1, tabDefaultMinWidth1]);

  const resetForm = () => {
    reset({
      fromLocation: '',
      toLocation: '',
      passengers: '',
      departureDate: '',
      departureTime: '',
      typeAircraft: '',
      multiCityInputs: ['', '', ''],
      multiCityDates: ['', ''],
      multiCityTimes: ['', ''],
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      returnDate: '',
      returnTime: '',
    });
  };

  const handleTabClick = (tab: TabType) => {
    if (tab === 'empty-leg') {
      router.push('/empty-leg-flights');
    } else {
      setActiveTab(tab);
      if (tab === 'multi-city') {
        setIsMultiCity(true);
      } else {
        setIsMultiCity(false);
        resetForm();
      }
    }
  };

  const handleMultiCityInputChange = (index: number, value: string) => {
    const newInputs = [...multiCityInputs];
    newInputs[index] = value;
    setValue('multiCityInputs', newInputs);
    // Trigger validation for specific multiCityInputs field
    trigger(`multiCityInputs.${index}` as keyof BookingFormData);
  };

  const handleAddPoint = () => {
    if (multiCityInputs.length < 5) {
      setValue('multiCityInputs', [...multiCityInputs, '']);
      const currentDates = getValues('multiCityDates');
      const currentTimes = getValues('multiCityTimes');
      setValue('multiCityDates', [...currentDates, '']);
      setValue('multiCityTimes', [...currentTimes, '']);
    }
  };

  const onFormSubmit: SubmitHandler<BookingFormData> = (data) => {
    if (isMultiCity) {
      // Filter out empty cities and format for submission
      const cities = data.multiCityInputs.filter((city) => city.trim() !== '');
      console.log({
        type: 'multi-city',
        cities,
      });
    } else {
      console.log({
        type: activeTab,
        from: data.fromLocation,
        to: data.toLocation,
        passengers: data.passengers,
        departureDate: data.departureDate,
        departureTime: data.departureTime,
        typeAircraft: data.typeAircraft,
      });
    }
  };

  const handleNext = async () => {
    let fieldsToValidate: (keyof BookingFormData)[] = [];

    if (activeTab === 'one-way') {
      fieldsToValidate = [
        'fromLocation',
        'toLocation',
        'passengers',
        'departureDate',
        'departureTime',
        'typeAircraft',
      ];
    } else if (activeTab === 'round-trip') {
      fieldsToValidate = [
        'fromLocation',
        'toLocation',
        'passengers',
        'departureDate',
        'departureTime',
        'typeAircraft',
        'returnDate',
        'returnTime',
      ];
    } else if (activeTab === 'multi-city') {
      fieldsToValidate = [
        'passengers',
        'typeAircraft',
      ];

      for (let i = 0; i < multiCityInputs.length; i++) {
        fieldsToValidate.push(`multiCityInputs.${i}` as keyof BookingFormData);
      }

      // Validate date/time for each leg (all cities except the last)
      for (let i = 0; i < multiCityInputs.length - 1; i++) {
        fieldsToValidate.push(`multiCityDates.${i}` as keyof BookingFormData);
        fieldsToValidate.push(`multiCityTimes.${i}` as keyof BookingFormData);
      }
    }

    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      setIsCalculatingPrice(true);
      try {
        const data = getValues();
        const payload = buildPricePayload(data, activeTab, multiCityInputs);

        const response = await fetch('/api/calculate-price', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const raw = await response.json() as CalculatePriceResponse;
        console.log('[calculate-price] response:', raw);
        setPriceData(raw);

        const filteredCities = data.multiCityInputs.filter((c) => c.trim() !== '');
        const summary: FlightSummary = {
          fromLocation: data.fromLocation,
          toLocation: data.toLocation,
          departureDate: data.departureDate,
          departureTime: data.departureTime,
          returnDate: data.returnDate,
          returnTime: data.returnTime,
          passengers: data.passengers,
          typeAircraft: data.typeAircraft,
          activeTab,
          multiCityInputs: filteredCities,
          multiCityDates: data.multiCityDates.slice(0, filteredCities.length - 1),
          multiCityTimes: data.multiCityTimes.slice(0, filteredCities.length - 1),
        };
        console.log('[BookingForm] flightSummary:', summary);
        setFlightSummary(summary);
      } catch (error) {
        console.error('[calculate-price] error:', error);
      } finally {
        setIsCalculatingPrice(false);
        setCurrentFormTab(2);
      }
    }
  };

  const handleSubmitQuote = async () => {
    // Validate all fields from the second tab
    const fieldsToValidate: (keyof BookingFormData)[] = [
      'firstName',
      'lastName',
      'email',
      'phone',
    ];

    // Trigger validation for second tab fields
    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      setIsSubmittingQuote(true);
      try {
        const allFormData = getValues();
        const fullName = `${allFormData.firstName} ${allFormData.lastName}`.trim();

        // 1. Submit lead to HeraFlight API
        console.log('[BookingForm] priceData:', priceData);
        console.log('[BookingForm] priceData.search_id:', priceData?.search_id);

        const leadsPayload: Record<string, unknown> = {
          contact: {
            name: fullName,
            email: allFormData.email,
            ...(allFormData.phone ? { phone: allFormData.phone } : {}),
          },
        };

        if (priceData?.search_id) {
          leadsPayload.search_id = priceData.search_id;
        }

        if (allFormData.typeAircraft) {
          const categoryKey = allFormData.typeAircraft
            .toLowerCase()
            .replace(/[-\s]+/g, '_');
          leadsPayload.aircraft_category = categoryKey;

          const sellerQuoteIds = (priceData?.aircraft_list ?? [])
            .filter((a) => a.category_code === categoryKey && a.avinode_quote_id)
            .map((a) => a.avinode_quote_id as string);

          if (sellerQuoteIds.length > 0) {
            leadsPayload.seller_quote_ids = sellerQuoteIds;
          }
        }

        const leadsResponse = await fetch('/api/leads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(leadsPayload),
        });

        if (!leadsResponse.ok) {
          console.error('[leads] failed:', await leadsResponse.text());
        } else {
          const leadsResult = await leadsResponse.json();
          console.log('[leads] submitted successfully:', leadsResult);
        }

        // 2. Submit to PipeDrive via maineForm API
        // Attribution: GTM hidden inputs (FormData) + cookie fallback from proxy.ts
        const attribution = getAttributionPayload(formRef.current);

        // Price for selected aircraft category (Pipedrive deal Value)
        const categoryKey = allFormData.typeAircraft
          ?.toLowerCase()
          .replace(/[-\s]+/g, '_');
        const dealValue =
          categoryKey && priceData?.prices
            ? priceData.prices[categoryKey]
            : undefined;

        // Unique aircraft prices as "min - max" string for the Pipedrive note
        const uniqueAircraftPrices = [
          ...new Set(
            (priceData?.aircraft_list ?? [])
              .filter((a) => !categoryKey || a.category_code === categoryKey)
              .map((a) => a.total_price)
              .filter((p): p is number => typeof p === 'number' && p > 0)
          ),
        ].sort((a, b) => a - b);
        const priceRange =
          uniqueAircraftPrices.length > 1
            ? uniqueAircraftPrices.join(' - ')
            : undefined;

        const submissionData = {
          firstName: allFormData.firstName,
          lastName: allFormData.lastName,
          email: allFormData.email,
          phone: allFormData.phone,
          activeTab: activeTab,
          fromLocation: allFormData.fromLocation,
          toLocation: allFormData.toLocation,
          passengers: allFormData.passengers,
          departureDate: allFormData.departureDate,
          departureTime: allFormData.departureTime,
          returnDate: allFormData.returnDate,
          returnTime: allFormData.returnTime,
          typeAircraft: allFormData.typeAircraft,
          multiCityInputs: allFormData.multiCityInputs.filter(
            (city) => city.trim() !== ''
          ),
          multiCityDates: allFormData.multiCityDates || [],
          multiCityTimes: allFormData.multiCityTimes || [],
          ...(typeof dealValue === 'number' ? { dealValue } : {}),
          ...(priceRange ? { priceRange } : {}),
          ...attribution,
        };

        const response = await fetch('/api/maineForm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submissionData),
        });

        if (response.ok) {
          const result = await response.json();
          console.log('Booking form submitted successfully:', result);
          resetForm();
          router.push('/thank-you-for-contact');
        } else {
          const error = await response.json();
          console.error('Error submitting booking form:', error);
          setIsError(true);
        }
      } catch (error) {
        console.error('Network error:', error);
        setIsError(true);
      } finally {
        setIsSubmittingQuote(false);
      }
    }
  };

  const handleCloseThankYou = () => {
    setCurrentFormTab(1);
  };

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(onFormSubmit)(e);
  };

  return (
    <section
      className={[styles.bookingform, className].join(' ')}
      data-device={device}
    >
      <form
        ref={formRef}
        className={styles.formOuter}
        onSubmit={handleFormSubmit}
      >
        <div className={styles.firstline}>
          <h3 className={styles.bookYourFlight}>Book your flight now</h3>
          {currentFormTab === 1 && (
            <nav className={styles.tabs} style={tabsStyle}>
              <div
                className={
                  activeTab === 'one-way' ? styles.tabactive : styles.tabdefault
                }
                style={activeTab === 'one-way' ? tabActiveStyle : tabDefaultStyle}
                onClick={() => handleTabClick('one-way')}
              >
                <div className={styles.item}>One Way</div>
              </div>
              <div
                className={
                  activeTab === 'round-trip' ? styles.tabactive : styles.tabdefault
                }
                style={
                  activeTab === 'round-trip' ? tabActiveStyle : tabDefaultStyle
                }
                onClick={() => handleTabClick('round-trip')}
              >
                <div className={styles.item1}>Round Trip</div>
              </div>
              <div
                className={
                  activeTab === 'multi-city' ? styles.tabactive : styles.tabdefault1
                }
                style={
                  activeTab === 'multi-city' ? tabActiveStyle : tabDefault1Style
                }
                onClick={() => handleTabClick('multi-city')}
              >
                <div className={styles.item2}>Multi-City</div>
              </div>
              <div
                className={
                  activeTab === 'empty-leg' ? styles.tabactive : styles.tabdefault1
                }
                style={
                  activeTab === 'empty-leg' ? tabActiveStyle : tabDefault1Style
                }
                onClick={() => handleTabClick('empty-leg')}
              >
                <div className={styles.item2}>Empty Leg</div>
              </div>
            </nav>
          )}
        </div>
        <div className={`${isMultiCity ? styles.formBlockMulti : styles.formblock}`}>
          {currentFormTab === 1 && (
            <CurrentTab1
              activeTab={activeTab}
              control={control}
              multiCityInputs={multiCityInputs}
              handleMultiCityInputChange={handleMultiCityInputChange}
              errors={errors}
              handleAddPoint={handleAddPoint}
              handleNext={handleNext}
              isLoading={isCalculatingPrice}
            />
          )}
          {currentFormTab === 2 && (
            <CurrentTab2
              control={control}
              errors={errors}
              handleSubmitQuote={handleSubmitQuote}
              isSubmitting={isSubmittingQuote}
              priceData={priceData}
              flightSummary={flightSummary}
            />
          )}
          {currentFormTab === 3 && (
            <CurrentTab3 handleCloseThankYou={handleCloseThankYou} />
          )}
        </div>
      </form>
      <div className={styles.formCurrentTabContainer}>
        <div className={styles.formCurrentTab}>
          <div
            className={`${styles.formCurrentTabItem} ${currentFormTab === 1 ? styles.active : ''}`}
          ></div>
          <div
            className={`${styles.formCurrentTabItem} ${currentFormTab === 2 ? styles.active : ''}`}
          ></div>
          <div
            className={`${styles.formCurrentTabItem} ${currentFormTab === 3 ? styles.active : ''}`}
          ></div>
        </div>
      </div>
      {isError && <ErrorModal onClose={() => setIsError(false)} />}
    </section>
  );
};

export default BookingForm;
